// 클라이언트 응답 캐시 — 탭 전환마다 같은 GET 을 새로 부르지 않도록 하는 얇은 계층.
// 단순 stale-while-revalidate: 신선(<maxAge)하면 캐시를 즉시 반환, 아니면 네트워크(동시 요청은 dedupe).
// 변경(추가/삭제/수정) 후에는 invalidate() 또는 setCache() 로 즉시 최신화한다.
// ※ 브라우저에서만 호출(클라이언트 컴포넌트). 상대경로 fetch 는 페이지 origin 기준으로 해석된다.

type Entry = { data: unknown; ts: number };

const cache = new Map<string, Entry>();
const inflight = new Map<string, Promise<unknown>>();

const DEFAULT_MAX_AGE = 60_000; // 60초 — 세션 내 탭 전환은 이 안이라 즉시 반환된다.

export async function cachedGet<T>(url: string, maxAgeMs = DEFAULT_MAX_AGE): Promise<T> {
  const hit = cache.get(url);
  if (hit && Date.now() - hit.ts < maxAgeMs) return hit.data as T;

  const existing = inflight.get(url);
  if (existing) return existing as Promise<T>;

  const p = (async () => {
    try {
      const r = await fetch(url, { cache: "no-store" });
      const data = await r.json();
      cache.set(url, { data, ts: Date.now() });
      return data as T;
    } finally {
      inflight.delete(url);
    }
  })();
  inflight.set(url, p);
  return p as Promise<T>;
}

// 앱 로드 시 미리 데워둔다(이미 신선하거나 진행 중이면 스킵). 실패는 무시.
export function warm(url: string, maxAgeMs = DEFAULT_MAX_AGE): void {
  const hit = cache.get(url);
  if (hit && Date.now() - hit.ts < maxAgeMs) return;
  if (inflight.has(url)) return;
  void cachedGet(url, maxAgeMs).catch(() => {});
}

// 변경 후 캐시 무효화 — 다음 요청 때 새로 가져온다.
export function invalidate(url: string): void {
  cache.delete(url);
  inflight.delete(url);
}

// 변경 결과를 캐시에 즉시 반영(낙관적) — 재진입 시에도 최신 상태가 즉시 뜨게 한다.
export function setCache<T>(url: string, data: T): void {
  cache.set(url, { data, ts: Date.now() });
}
